import { useState, useCallback, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useAddPlanLine } from '@/hooks/usePlanDetail'
import { usePlanMetadata } from '@/hooks/usePlanMetadata'
import { useCurrentVersionId } from '@/hooks/useCurrentVersionId'
import { useExportPlanLines } from '@/hooks/useExportPlanLines'
import { PlanDetailHeader } from '@/components/PlanDetail/PlanDetailHeader'
import {
  PlanDetailTabs,
  TabValue,
} from '@/components/PlanDetail/PlanDetailTabs'
import { ProductsTable } from '@/components/PlanDetail/ProductsTable'
import { ImportItemsModal } from '@/components/PlanDetail/ImportItemsModal'
import { toast } from '@/stores/useToastStore'
import { buildHierarchyBreadcrumb } from '@/utils/formatters'
import { CommentsTab } from '@/components/PlanDetail/CommentsTab'
import { PlanFinancialsTab } from '@/components/PlanDetail/PlanFinancialsTab'
import { PlanSummaryTab } from '@/components/PlanDetail/PlanSummaryTab'
import { AttributesReportMockup } from '@/components/Dashboard/AttributesReportMockup'

const validTabs: TabValue[] = [
  'products',
  'summary',
  'financials',
  'analytics',
  'comments',
]

export default function AssortmentPlanDetail() {
  const { planId } = useParams<{ planId: string }>()
  const [searchParams] = useSearchParams()
  const [activeTab, setActiveTab] = useState<TabValue>(() => {
    const tab = searchParams.get('tab') as TabValue | null
    return tab && validTabs.includes(tab) ? tab : 'products'
  })

  useEffect(() => {
    const tab = searchParams.get('tab') as TabValue | null
    if (tab && validTabs.includes(tab)) {
      setActiveTab(tab)
    } else if (!tab) {
      setActiveTab('products')
    }
  }, [searchParams])

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [importModalOpen, setImportModalOpen] = useState(false)
  const queryClient = useQueryClient()

  const { versionId, isViewingNonActiveVersion } = useCurrentVersionId(planId)

  // Fetch only plan metadata (no lines)
  const { data, isLoading } = usePlanMetadata(planId!)

  const { exportPlanLines, isProcessing: isExporting } = useExportPlanLines(
    planId!,
    versionId ?? null
  )
  const isReadOnly =
    !isViewingNonActiveVersion &&
    (data?.status === 'SUBMITTED' || data?.status === 'APPROVED')

  const addLineMutation = useAddPlanLine(planId!, data?.hierarchy)

  const handleAddItem = () => {
    addLineMutation.mutate(undefined, {
      onSuccess: () => {
        setPage(1)
        queryClient.invalidateQueries({
          queryKey: ['plan-detail', planId],
        })
        // Also invalidate DAG table rows so new line shows up
        queryClient.invalidateQueries({
          queryKey: ['dag-table', 'rows', planId],
        })
      },
      onError: (error: Error) => {
        toast.error(`Failed to add line: ${error.message}`)
      },
    })
  }

  // Reset page when pageSize changes
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setPage(1)
  }

  // Reset to page 1 when active version changes
  useEffect(() => {
    setPage(1)
  }, [versionId])

  // Handle import completion - reset to page 1 to see imported items
  const handleImportComplete = () => {
    setPage(1)
  }

  // Handle bulk delete success - reset to page 1 to avoid empty page
  const handleBulkDeleteSuccess = () => {
    setPage(1)
  }

  // Handle export
  const handleExport = useCallback(() => {
    exportPlanLines()
  }, [exportPlanLines])

  if (!planId) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        Invalid plan ID
      </div>
    )
  }

  // Only show "Plan not found" when we're not loading and have no data
  if (!isLoading && !data) {
    return (
      <div className="flex items-center justify-center p-12 text-muted-foreground">
        Plan not found
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Header */}
      <PlanDetailHeader
        planId={planId}
        name={data?.name ?? ''}
        status={data?.status ?? 'DRAFT'}
        planMetadata={data}
        hierarchyBreadcrumb={
          data?.hierarchy ? buildHierarchyBreadcrumb(data.hierarchy) : ''
        }
      />

      {/* Tabs */}
      <PlanDetailTabs activeTab={activeTab} onTabChange={setActiveTab} />

      {/* Tab content */}
      {activeTab === 'products' ? (
        <ProductsTable
          planId={planId}
          versionId={versionId}
          primaryAttributes={
            data?.primary_attributes ?? data?.primary_attribute_ids ?? []
          }
          page={page}
          pageSize={pageSize}
          onPageChange={setPage}
          onPageSizeChange={handlePageSizeChange}
          onAddItem={handleAddItem}
          onImport={() => setImportModalOpen(true)}
          onExport={handleExport}
          isExporting={isExporting}
          onBulkDeleteSuccess={handleBulkDeleteSuccess}
          isReadOnly={isReadOnly}
          planStatus={data?.status}
        />
      ) : activeTab === 'summary' ? (
        <PlanSummaryTab planId={planId} />
      ) : activeTab === 'financials' ? (
        <PlanFinancialsTab planId={planId} />
      ) : activeTab === 'analytics' ? (
        <AttributesReportMockup
          lockedPlanId={planId}
          lockedPlanName={data?.name}
        />
      ) : activeTab === 'comments' ? (
        <CommentsTab planId={planId} />
      ) : null}

      {/* Import Items Modal */}
      <ImportItemsModal
        open={importModalOpen}
        onOpenChange={setImportModalOpen}
        planId={planId}
        versionId={versionId!}
        onImportComplete={handleImportComplete}
      />
    </div>
  )
}
